/**
 * Column re-ordering for the wide grids (Securities, Portfolios → View portfolio; HP-V2,
 * Pedro 2026-10-19: "drag headings + the Columns list, with Reset order; on both").
 *
 * The order is a list of column keys remembered on this computer (localStorage, per grid).
 * Columns the stored order does not know (a new attribute, a lens added to the view) are
 * slotted in after their neighbour in the default order, so a stored order never hides one.
 * The headings are dragged with the browser's own drag and drop; the Columns list uses
 * @hello-pangea/dnd, as the other ordered lists of the app do.
 */
import { useEffect, useMemo, useState } from 'react';
import type React from 'react';

/** The stored order merged onto the current default order (pure). */
export function mergeOrder(stored: string[] | null, defaults: string[]): string[] {
  if (!stored) return defaults;
  const known = new Set(defaults);
  const out = stored.filter((k, i) => known.has(k) && stored.indexOf(k) === i);
  for (let i = 0; i < defaults.length; i++) {
    const d = defaults[i];
    if (out.includes(d)) continue;
    let at = 0;
    for (let j = i - 1; j >= 0; j--) {
      const p = out.indexOf(defaults[j]);
      if (p >= 0) { at = p + 1; break; }
    }
    out.splice(at, 0, d);
  }
  return out;
}

/** Move `key` to just before (or after) `target` in `order` (pure). */
export function moveNextTo(order: string[], key: string, target: string, after: boolean): string[] {
  if (key === target) return order;
  const rest = order.filter((k) => k !== key);
  const t = rest.indexOf(target);
  if (t < 0) return order;
  rest.splice(after ? t + 1 : t, 0, key);
  return rest;
}

export function useColumnOrder(storageKey: string, defaultKeys: string[]) {
  const [stored, setStored] = useState<string[] | null>(() => {
    try { const v = localStorage.getItem(storageKey); return v ? (JSON.parse(v) as string[]) : null; }
    catch { return null; }
  });
  useEffect(() => {
    try {
      if (stored) localStorage.setItem(storageKey, JSON.stringify(stored));
      else localStorage.removeItem(storageKey);
    } catch { /* private window */ }
  }, [stored, storageKey]);
  const sig = defaultKeys.join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const order = useMemo(() => mergeOrder(stored, defaultKeys), [stored, sig]);
  return {
    order,
    custom: stored != null,
    /** Drop `key` next to `target` (before it, or after it). */
    place: (key: string, target: string, after: boolean) =>
      setStored(moveNextTo(mergeOrder(stored, defaultKeys), key, target, after)),
    reset: () => setStored(null),
  };
}

/** Column widths set by dragging a heading's right edge (HP-V4, Pedro 2026-10-19: "no way to
 *  manually widen the column"), remembered on this computer per grid. A column without a
 *  stored width keeps its default. */
export const MIN_COL_WIDTH = 50;
export function useColumnWidths(storageKey: string) {
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    try { const v = localStorage.getItem(storageKey); return v ? (JSON.parse(v) as Record<string, number>) : {}; }
    catch { return {}; }
  });
  useEffect(() => {
    try {
      if (Object.keys(widths).length) localStorage.setItem(storageKey, JSON.stringify(widths));
      else localStorage.removeItem(storageKey);
    } catch { /* private window */ }
  }, [widths, storageKey]);
  return {
    widthOf: (key: string, fallback: number) => widths[key] ?? fallback,
    setWidth: (key: string, w: number) =>
      setWidths((p) => ({ ...p, [key]: Math.max(MIN_COL_WIDTH, Math.round(w)) })),
    custom: Object.keys(widths).length > 0,
    reset: () => setWidths({}),
  };
}

/** Apply an order of keys to a list of columns. */
export function ordered<T extends { key: string }>(cols: T[], order: string[]): T[] {
  const by = new Map(cols.map((c) => [c.key, c]));
  return order.map((k) => by.get(k)).filter((c): c is T => !!c);
}

/** Drag a heading onto another: before it if dropped on its left half, after it on its right. */
export function useHeadingDrag(place: (key: string, target: string, after: boolean) => void) {
  const [drag, setDrag] = useState<string | null>(null);
  const [over, setOver] = useState<{ key: string; after: boolean } | null>(null);
  const props = (key: string): React.ThHTMLAttributes<HTMLTableCellElement> => ({
    draggable: true,
    onDragStart: (e) => { setDrag(key); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', key); },
    onDragOver: (e) => {
      if (!drag) return;
      e.preventDefault();
      const r = e.currentTarget.getBoundingClientRect();
      const after = e.clientX > r.left + r.width / 2;
      if (!over || over.key !== key || over.after !== after) setOver({ key, after });
    },
    onDrop: (e) => {
      e.preventDefault();
      if (drag && over) place(drag, over.key, over.after);
      setDrag(null); setOver(null);
    },
    onDragEnd: () => { setDrag(null); setOver(null); },
  });
  /** The drop marker: a thick blue edge on the side the column will land. */
  const marker = (key: string): React.CSSProperties => {
    if (!over || over.key !== key || drag === key) return drag === key ? { opacity: 0.5 } : {};
    const edge = '3px solid var(--mantine-color-azul-6)';
    return over.after ? { borderRight: edge } : { borderLeft: edge };
  };
  return { props, marker };
}
